// frontend\src\pages\DevTimer.js
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import ScrollToTopButton from "../components/ScrollToTopButton";
import {
  BiArrowBack,
  BiPlay,
  BiPause,
  BiSkipNext,
  BiCheckCircle,
  BiInfoCircle,
} from "react-icons/bi";

const AGITATION_TYPES = {
  STANDARD: "standard",
  ROTARY: "rotary",
  STAND: "stand",
};

const PROCESS_TYPES = {
  bw: { name: "Чёрно-белая", description: "Классическая чёрно-белая проявка" },
  bw_reversal: {
    name: "Ч/Б обращаемая",
    description: "Обращаемая чёрно-белая проявка",
  },
  e6: { name: "E-6 (Слайд)", description: "Проявка слайдовых плёнок" },
  c41: { name: "C-41 (Негатив)", description: "Проявка цветных негативов" },
};

const DevTimer = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [activeStageIndex, setActiveStageIndex] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [startTimestamp, setStartTimestamp] = useState(null);
  const [pauseTimestamp, setPauseTimestamp] = useState(null);
  const [totalPausedTime, setTotalPausedTime] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [autoAdvanceEnabled, setAutoAdvanceEnabled] = useState(false);
  const [autoAdvanceDelay, setAutoAdvanceDelay] = useState(5);
  const [autoAdvanceCountdown, setAutoAdvanceCountdown] = useState(0);
  const [agitationActive, setAgitationActive] = useState(false);
  const [showProcessInfo, setShowProcessInfo] = useState(false);

  const timerRef = useRef(null);
  const agitationTimerRef = useRef(null);
  const autoAdvanceTimerRef = useRef(null);
  const agitStartAudioRef = useRef(null);
  const timerStopAudioRef = useRef(null);
  const agitStopAudioRef = useRef(null);

  const lastAgitationStart = useRef(0);
  const firstMinuteAgitationDone = useRef(false);
  const lastMinuteAgitationDone = useRef(false);
  const agitationRunning = useRef(false);

  // Инициализация аудио элементов
  useEffect(() => {
    const initAudio = (ref) => {
      if (ref.current) {
        ref.current.preload = "auto";
        ref.current.load();
        ref.current.addEventListener("error", (e) => {
          console.error("Audio error:", e);
        });
      }
    };

    initAudio(agitStartAudioRef);
    initAudio(timerStopAudioRef);
    initAudio(agitStopAudioRef);

    return () => {
      [agitStartAudioRef, timerStopAudioRef, agitStopAudioRef].forEach(
        (ref) => {
          if (ref.current) {
            ref.current.removeEventListener("error", () => {});
            ref.current.pause();
            ref.current = null;
          }
        }
      );
    };
  }, []);

  const activeStages = useMemo(() => {
    return (state?.stages || [])
      .filter((stage) => stage.enabled)
      .map((stage) => ({
        ...stage,
        agitationType: stage.agitationType || AGITATION_TYPES.STANDARD,
        agitation:
          stage.agitationType !== AGITATION_TYPES.STAND
            ? stage.agitation
            : null,
      }));
  }, [state?.stages]);

  const currentStage = activeStages[activeStageIndex];
  const isLastStage = activeStageIndex === activeStages.length - 1;
  const processType = state?.processType || "bw";

  const totalStageTime = useMemo(() => {
    return (currentStage?.minutes * 60 || 0) + (currentStage?.seconds || 0);
  }, [currentStage]);

  useEffect(() => {
    if (state?.autoAdvance !== undefined) {
      setAutoAdvanceEnabled(state.autoAdvance);
      setAutoAdvanceDelay(state.advanceDelay);
    }
  }, [state]);

  const calculateElapsedTime = useCallback(() => {
    if (!startTimestamp) return 0;
    const now = Date.now();
    const pausedTime = pauseTimestamp
      ? totalPausedTime + (now - pauseTimestamp)
      : totalPausedTime;
    return Math.floor((now - startTimestamp - pausedTime) / 1000);
  }, [startTimestamp, pauseTimestamp, totalPausedTime]);

  const calculateTimeLeft = useCallback(() => {
    return Math.max(0, totalStageTime - calculateElapsedTime());
  }, [totalStageTime, calculateElapsedTime]);

  const resetTimer = useCallback(() => {
    clearInterval(timerRef.current);
    clearTimeout(agitationTimerRef.current);
    clearInterval(autoAdvanceTimerRef.current);
    setIsRunning(false);
    setStartTimestamp(null);
    setPauseTimestamp(null);
    setTotalPausedTime(0);
    setTimeLeft(totalStageTime);
    setAgitationActive(false);
    setAutoAdvanceCountdown(0);
    lastAgitationStart.current = 0;
    firstMinuteAgitationDone.current = false;
    lastMinuteAgitationDone.current = false;
    agitationRunning.current = false;
  }, [totalStageTime]);

  useEffect(() => {
    if (!currentStage) {
      navigate("/timer-setup");
      return;
    }
    resetTimer();
  }, [currentStage, navigate, resetTimer]);

  const safePlayAudio = useCallback((ref) => {
    if (!ref.current) return;

    try {
      ref.current.currentTime = 0;
      ref.current.play().catch((e) => {
        console.log("Audio play interrupted:", e);
        // Попробуем еще раз через небольшой интервал
        setTimeout(() => {
          ref.current?.play().catch((e) => console.log("Retry failed:", e));
        }, 300);
      });
    } catch (e) {
      console.error("Audio play error:", e);
    }
  }, []);

  const playTimerStopSound = useCallback(() => {
    safePlayAudio(timerStopAudioRef);
  }, [safePlayAudio]);

  const stopAgitation = useCallback(() => {
    if (!agitationRunning.current) return;
    console.log(`Агитация закончилась в ${new Date().toLocaleTimeString()}`);
    agitationRunning.current = false;
    setAgitationActive(false);
    clearTimeout(agitationTimerRef.current);
    safePlayAudio(agitStopAudioRef);
  }, [safePlayAudio]);

  const startAgitation = useCallback(
    (duration) => {
      if (
        agitationRunning.current ||
        !isRunning ||
        !currentStage ||
        currentStage.agitationType === AGITATION_TYPES.STAND
      ) {
        console.log(
          "Агитация не запущена:",
          agitationRunning.current ? "уже запущена" : "",
          !isRunning ? "таймер не работает" : "",
          !currentStage ? "нет текущего этапа" : "",
          currentStage?.agitationType === AGITATION_TYPES.STAND
            ? "режим STAND"
            : ""
        );
        return;
      }

      console.log(
        `Агитация началась в ${new Date().toLocaleTimeString()}, продолжительность: ${duration} сек`
      );
      agitationRunning.current = true;
      setAgitationActive(true);
      lastAgitationStart.current = Date.now();
      safePlayAudio(agitStartAudioRef);
      agitationTimerRef.current = setTimeout(() => {
        if (isRunning) stopAgitation();
      }, duration * 1000);
    },
    [isRunning, stopAgitation, currentStage, safePlayAudio]
  );

  const startTimer = useCallback(() => {
    if (isRunning || !currentStage) return;

    const now = Date.now();
    setIsRunning(true);

    console.log(`Таймер запущен: ${new Date(now).toLocaleTimeString()}`);
    console.log(`  Длительность этапа: ${totalStageTime} сек`);
    console.log(`  Тип агитации: ${currentStage.agitationType}`);

    if (currentStage.agitationType !== AGITATION_TYPES.STAND) {
      console.log(`  Параметры агитации:`);
      console.log(`    firstMinute: ${currentStage.agitation?.firstMinute}`);
      console.log(`    interval: ${currentStage.agitation?.interval}`);
      console.log(`    duration: ${currentStage.agitation?.duration}`);
      console.log(`    lastMinute: ${currentStage.agitation?.lastMinute}`);
    }

    if (pauseTimestamp) {
      setTotalPausedTime((prev) => prev + (now - pauseTimestamp));
      setPauseTimestamp(null);
    } else {
      setStartTimestamp(now);
    }

    setAutoAdvanceCountdown(0);
    clearInterval(autoAdvanceTimerRef.current);
    firstMinuteAgitationDone.current = false;
    lastMinuteAgitationDone.current = false;
  }, [currentStage, isRunning, pauseTimestamp, totalStageTime]);

  const pauseTimer = useCallback(() => {
    console.log(`Таймер на паузе: ${new Date().toLocaleTimeString()}`);
    clearInterval(timerRef.current);
    clearTimeout(agitationTimerRef.current);
    setIsRunning(false);
    setPauseTimestamp(Date.now());
    if (agitationRunning.current) {
      console.log("Агитация принудительно остановлена при паузе");
      stopAgitation();
    }
    setAutoAdvanceCountdown(0);
    clearInterval(autoAdvanceTimerRef.current);
  }, [stopAgitation]);

  const handleNext = useCallback(() => {
    if (isLastStage) {
      console.log("Процесс проявки завершен!");
      navigate("/timer-setup");
      toast.success("Процесс проявки завершен!");
    } else {
      setActiveStageIndex((prev) => prev + 1);
      resetTimer();
    }
  }, [isLastStage, navigate, resetTimer]);

  useEffect(() => {
    if (!autoAdvanceEnabled) return;

    if (
      (activeStageIndex === 0 && !startTimestamp) ||
      (activeStageIndex > 0 && !isRunning && !startTimestamp)
    ) {
      setAutoAdvanceCountdown(autoAdvanceDelay);
      autoAdvanceTimerRef.current = setInterval(() => {
        setAutoAdvanceCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(autoAdvanceTimerRef.current);
            startTimer();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (timeLeft === 0) {
      setAutoAdvanceCountdown(autoAdvanceDelay);
      autoAdvanceTimerRef.current = setInterval(() => {
        setAutoAdvanceCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(autoAdvanceTimerRef.current);
            handleNext();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(autoAdvanceTimerRef.current);
  }, [
    autoAdvanceEnabled,
    autoAdvanceDelay,
    activeStageIndex,
    isRunning,
    startTimestamp,
    timeLeft,
    startTimer,
    handleNext,
  ]);

  useEffect(() => {
    if (!isRunning || timeLeft <= 0 || !currentStage) return;

    timerRef.current = setInterval(() => {
      const elapsed = calculateElapsedTime();
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      if (newTimeLeft <= 0) {
        console.log(`Этап завершён в ${new Date().toLocaleTimeString()}`);
        clearInterval(timerRef.current);
        setIsRunning(false);
        setAgitationActive(false);
        playTimerStopSound();
        return;
      }

      // Логика агитации (только если не STAND режим)
      if (currentStage.agitationType !== AGITATION_TYPES.STAND) {
        // Для ROTARY - постоянная агитация
        if (currentStage.agitationType === AGITATION_TYPES.ROTARY) {
          if (!agitationRunning.current) {
            startAgitation(currentStage.agitation.duration);
          }
          return;
        }

        // Для STANDARD - обычная агитация по расписанию
        if (currentStage.agitationType === AGITATION_TYPES.STANDARD) {
          const { firstMinute, lastMinute, interval, duration } =
            currentStage.agitation;

          // Первая агитация
          if (
            firstMinute &&
            !firstMinuteAgitationDone.current &&
            elapsed <= firstMinute
          ) {
            startAgitation(firstMinute);
            firstMinuteAgitationDone.current = true;
            return;
          }

          // Промежуточная агитация
          if (
            interval &&
            duration &&
            elapsed > firstMinute &&
            newTimeLeft > lastMinute + duration
          ) {
            const timeSinceFirstAgitation = elapsed - firstMinute;
            if (
              timeSinceFirstAgitation % interval <= 0.3 &&
              Date.now() - lastAgitationStart.current >= interval * 1000
            ) {
              startAgitation(duration);
              return;
            }
          }

          // Финальная агитация
          if (
            lastMinute &&
            newTimeLeft <= lastMinute &&
            !lastMinuteAgitationDone.current
          ) {
            startAgitation(Math.min(duration, newTimeLeft));
            lastMinuteAgitationDone.current = true;
          }
        }
      }
    }, 200);

    return () => clearInterval(timerRef.current);
  }, [
    isRunning,
    currentStage,
    totalStageTime,
    calculateElapsedTime,
    calculateTimeLeft,
    playTimerStopSound,
    startAgitation,
    timeLeft,
  ]);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const formatElapsedTime = useCallback(() => {
    return formatTime(calculateElapsedTime());
  }, [calculateElapsedTime]);

  const renderAgitationParams = () => {
    if (currentStage.agitationType === AGITATION_TYPES.STAND) {
      return (
        <div className="mt-3">
          <p>Режим без агитации (stand development)</p>
        </div>
      );
    }

    const { firstMinute, interval, duration, lastMinute } =
      currentStage.agitation || {};
    return (
      <div className="mt-3">
        <h5>Параметры агитации:</h5>
        <ul className="list-unstyled">
          <li>
            <strong>Тип:</strong>{" "}
            {currentStage.agitationType === AGITATION_TYPES.ROTARY
              ? "Постоянная"
              : "Стандартная"}
          </li>
          {currentStage.agitationType === AGITATION_TYPES.STANDARD && (
            <>
              <li>
                <strong>Первая агитация:</strong> {firstMinute} сек
              </li>
              <li>
                <strong>Интервал:</strong> каждые {interval} сек
              </li>
              <li>
                <strong>Длительность:</strong> {duration} сек
              </li>
              <li>
                <strong>Финальная агитация:</strong> последние {lastMinute} сек
              </li>
            </>
          )}
          {currentStage.agitationType === AGITATION_TYPES.ROTARY && (
            <li>
              <strong>Длительность:</strong> постоянная ({duration} сек)
            </li>
          )}
        </ul>
      </div>
    );
  };

  const renderProcessInfo = () => {
    const process = PROCESS_TYPES[processType] || {
      name: "Неизвестный процесс",
      description: "",
    };
    return (
      <div className="position-relative">
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={() => setShowProcessInfo(!showProcessInfo)}
        >
          <BiInfoCircle className="me-1" />
          {process.name}
        </button>
        {showProcessInfo && (
          <div
            className="position-absolute p-3 border rounded shadow-sm mt-1"
            style={{ 
              zIndex: 1000, 
              minWidth: "250px",
              backgroundColor: "var(--card-bg)",
              borderColor: "var(--border-color)",
              color: "var(--text-primary)"
            }}
          >
            <h6 style={{ color: "var(--text-primary)" }}>{process.name}</h6>
            <p style={{ color: "var(--text-primary)" }}>{process.description}</p>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setShowProcessInfo(false)}
              style={{
                color: "var(--btn-outline-primary-color)",
                borderColor: "var(--btn-outline-primary-border)"
              }}
            >
              Закрыть
            </button>
          </div>
        )}
      </div>
    );
  };

  if (!currentStage) return null;

  const handleButtonClick = () => {
    if (timeLeft === 0) {
      handleNext();
    } else {
      isRunning ? pauseTimer() : startTimer();
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <button
          className="btn btn-secondary"
          onClick={() => {
            resetTimer();
            navigate("/timer-setup");
          }}
        >
          <BiArrowBack className="me-1" /> К настройкам
        </button>
        {renderProcessInfo()}
      </div>

      <div className="card shadow">
        <div className="card-body">
          <div className="progress mb-4" style={{ height: "24px" }}>
            <div
              className="progress-bar"
              style={{
                width: `${(
                  ((activeStageIndex + (1 - timeLeft / totalStageTime)) * 100) /
                  activeStages.length
                ).toFixed(2)}%`,
              }}
            >
              Этап {activeStageIndex + 1} из {activeStages.length}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <h2 className="mb-3" style={{ color: "var(--text-primary)" }}>{currentStage.name}</h2>
              <p className="text-muted">{currentStage.description}</p>

              <div className="mt-3">
                <h5 style={{ color: "var(--text-primary)" }}>Длительность этапа:</h5>
                <p style={{ color: "var(--text-primary)" }}>{formatTime(totalStageTime)}</p>
              </div>

              {renderAgitationParams()}
            </div>

            <div className="col-md-6">
              <div className="text-center mb-4">
                <div className="display-2 fw-bold mb-2">
                  <span className={agitationActive ? "agitation-active" : ""}>
                    {formatTime(timeLeft)}
                  </span>
                </div>
                <p className="text-muted">Прошло: {formatElapsedTime()}</p>

                {agitationActive && (
                  <div className="alert alert-danger d-inline-block">
                    {currentStage.agitationType === AGITATION_TYPES.ROTARY
                      ? "Постоянная агитация (ротационная)"
                      : "Агитируйте раствор!"}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-center gap-3">
            <button
              className="btn btn-primary px-4 py-2"
              onClick={handleButtonClick}
            >
              {timeLeft === 0 ? (
                isLastStage ? (
                  <>
                    <BiCheckCircle className="me-1" /> Завершить
                  </>
                ) : (
                  <>
                    <BiSkipNext className="me-1" /> Следующий этап
                  </>
                )
              ) : isRunning ? (
                <>
                  <BiPause className="me-1" /> Пауза
                </>
              ) : (
                <>
                  <BiPlay className="me-1" /> Старт
                </>
              )}
            </button>
          </div>

          {autoAdvanceEnabled && autoAdvanceCountdown > 0 && (
            <div className="text-center mt-3">
              <small className="text-muted">
                {timeLeft === totalStageTime && !startTimestamp
                  ? `Старт через: ${autoAdvanceCountdown} сек`
                  : `Переход на следующий этап через: ${autoAdvanceCountdown} сек`}
              </small>
            </div>
          )}
        </div>
        <ScrollToTopButton/>
      </div>

      <audio
        ref={agitStartAudioRef}
        src="/sounds/agit-start.mp3"
        preload="auto"
      />
      <audio
        ref={timerStopAudioRef}
        src="/sounds/timer-stop.mp3"
        preload="auto"
      />
      <audio
        ref={agitStopAudioRef}
        src="/sounds/agit-stop.mp3"
        preload="auto"
      />
    </div>
  );
};

export default DevTimer;
